import { useCallback, useEffect, useRef, useState } from 'react';

import {
  getUnreadNotificationCount,
  listNotifications,
  markAllNotificationsRead,
  markNotificationRead,
} from '../api/notifications.client';
import type { SupportNotification } from '../types';
import {
  isTutorLiveSessionActive,
  subscribeTutorLiveSessionActive,
} from '../../tutor/liveSessionActivity';
import { acquireSupportNotificationsUnreadPoll } from './supportNotificationsSharedPoll';

export function useSupportNotifications(options?: { enabled?: boolean }) {
  const enabled = options?.enabled !== false;
  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState<SupportNotification[]>([]);
  const [loadingList, setLoadingList] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [liveActive, setLiveActive] = useState(() => isTutorLiveSessionActive());
  const mountedRef = useRef(true);

  const refreshUnread = useCallback(async () => {
    if (!enabled) return;
    try {
      const data = await getUnreadNotificationCount();
      if (mountedRef.current) {
        setUnread(data.unread);
        setError(null);
      }
    } catch {
      if (mountedRef.current) {
        setError('Could not load notifications');
      }
    }
  }, [enabled]);

  const refreshList = useCallback(async () => {
    if (!enabled) return;
    setLoadingList(true);
    try {
      const data = await listNotifications({ page: 1, page_size: 20 });
      if (mountedRef.current) {
        setItems(data.items);
        setError(null);
      }
    } catch {
      if (mountedRef.current) {
        setError('Could not load notifications');
      }
    } finally {
      if (mountedRef.current) {
        setLoadingList(false);
      }
    }
  }, [enabled]);

  const markRead = useCallback(
    async (notificationId: string) => {
      await markNotificationRead(notificationId);
      setItems((prev) =>
        prev.map((n) => (n.id === notificationId ? { ...n, is_read: true } : n)),
      );
      setUnread((prev) => Math.max(0, prev - 1));
    },
    [],
  );

  const markAllRead = useCallback(async () => {
    const result = await markAllNotificationsRead();
    setItems((prev) => prev.map((n) => ({ ...n, is_read: true })));
    setUnread(0);
    return result.updated;
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    return subscribeTutorLiveSessionActive(() => {
      setLiveActive(isTutorLiveSessionActive());
    });
  }, []);

  useEffect(() => {
    if (!enabled || liveActive) return undefined;
    const onUnread = (count: number) => {
      if (!mountedRef.current) return;
      setUnread(count);
      setError(null);
    };
    return acquireSupportNotificationsUnreadPoll(onUnread, {
      enabled: true,
      pauseWhileLive: true,
    });
  }, [enabled, liveActive]);

  return {
    unread,
    items,
    loadingList,
    error,
    refreshUnread,
    refreshList,
    markRead,
    markAllRead,
  };
}
